import React, { useState, useRef, useEffect } from "react";
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import { AppSwitch } from "Components/Generic/AppSwitch";
import { useTranslation } from "react-i18next";
import { MenuItem, ThirdLevelListProps } from "../types";
import { useHistory } from "react-router-dom";
import SvgIconUrl from "../../../helpers/SvgIcon/SvgIconUrl";

import { Divider, IsoText, StausChip, StatusChipText } from "./styles";
import { useAppState } from "Context/StateContext";
import { useQuery } from "@tanstack/react-query";
import OnBoardingService from "Services/onBoarding.service";
import { AxiosResponse } from "axios";
import { OnboardingStage } from "Models/onboarding.stage";
import { getBrandCustom } from "Util/getCustomColor";

const BACKICON = require("Assets/icons/arrow-back.svg");
const SECURITYICON = require("Assets/icons/security.svg");

type Props = ThirdLevelListProps & {
  containerRef?: React.RefObject<HTMLUListElement>;
  onRequestClose?: () => void;
};

const ThirdLevelList: React.FC<Props> = ({
  openNode,
  pinned,
  left,
  onClose,
  containerRef,
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useAppState();

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [onboardingStage, setOnboardingStage] = useState<OnboardingStage | undefined>();

  const internalListRef = useRef<HTMLUListElement | null>(null);
  const listRef = containerRef ?? internalListRef;

  const navigateSmart = (path?: string, state?: any) => {
    if (!path) return;

    onClose?.();
    onRequestClose?.();

    if (history.location.pathname === path) {
      history.replace(path, state);
    } else {
      history.push(path, state);
    }
  };

  const { refetch: refetchIsoCertificate } = useQuery(
    ["onboarding.get", currentUser?.companyId],
    () =>
      OnBoardingService.getAll({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response: AxiosResponse<OnboardingStage[]>) => {
        if (response.data && !!response.data.length) {
          setOnboardingStage(response.data[0]);
        }
      },
    }
  );

  const isInventoryActive = Boolean(onboardingStage && !onboardingStage.inventoryWasUploaded);

  useEffect(() => {
    const container = listRef.current;
    if (!container) return;

    const selector = 'button.Mui-selected[data-level="third"], button.Mui-selected';
    const selectedEl = container.querySelector(selector) as HTMLElement | null;

    if (selectedEl) {
      try {
        selectedEl.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      } catch {
        selectedEl.scrollIntoView?.({ block: "nearest", inline: "nearest" } as any);
      }
    }
  }, [activeKey, openNode, listRef]);

  const selectedPill = {
    backgroundColor: getBrandCustom("headerBackgroundColor", "#F1F6FB"),
    color: getBrandCustom("primaryColor", "#0055A4"),
  } as const;
  const hoverPill = {
    backgroundColor: getBrandCustom("headerBackgroundColor", "#F2F4F7"),
    color: getBrandCustom("primaryColor", "#1D2939"),
  } as const;

  const renderLeaf = (leaf: MenuItem) => {
    const isActive = activeKey === leaf.key;

    return (
      <ListItemButton
        key={leaf.key}
        selected={isActive}
        onClick={() => {
          setActiveKey(leaf.key);
          navigateSmart(leaf.path, leaf.state);
        }}
        sx={{
          borderRadius: 1,
          mx: 1,
          fontFamily: "Manrope",
          fontWeight: 600,
          fontSize: 15,
          color: isActive ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
          "&.Mui-selected": selectedPill,
          ...(isActive ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
        }}
      >
        {leaf.icon && (
          <ListItemIcon sx={{ minWidth: 17, mr: 1.5, color: "inherit" }}>
            <SvgIconUrl src={leaf.icon} size={17} />
          </ListItemIcon>
        )}
        <ListItemText
          primary={t(leaf.label) ?? leaf.label ?? ""}
          primaryTypographyProps={{ noWrap: true, title: (t(leaf.label) ?? leaf.label ?? "") as string }}
        />
      </ListItemButton>
    );
  };

  const hasChildren = !!openNode?.children?.length;

  const headerEl = hasChildren ? (
    <Box
      sx={{ display: "flex", alignItems: "center", ml: "8px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <ListItemIcon
        sx={{
          color: getBrandCustom("primaryColor", "#0055A4"),
          cursor: "pointer",
          minWidth: 17,
          mr: "14px",
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <SvgIconUrl src={BACKICON} size={22} />
      </ListItemIcon>
      <ListItemText
        primary={t(openNode!.sectionTitle ?? openNode!.label ?? "")}
        primaryTypographyProps={{
          noWrap: true,
          title: (t(openNode!.sectionTitle ?? openNode!.label ?? "") as string),
          fontSize: 16,
          fontWeight: 500,
          letterSpacing: 0.64,
          textTransform: "uppercase",
          color: getBrandCustom("primaryColor", "#0055A4"),
        }}
      />
    </Box>
  ) : null;

  const resolvedTitle =
    (openNode &&
      (t(openNode.sectionTitle ?? openNode.label ?? "") ||
        (openNode.sectionTitle ?? openNode.label ?? ""))) ||
    "";
  const showFooter = openNode?.key === "inventory";

  if (pinned) {
    return hasChildren ? (
      <Box
        data-sidebar="third"
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          top: 0,
          left,
          width: 300,
          height: "100vh",
          p: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          zIndex: 1100,
          backgroundColor: "#F9FAFB",
          boxShadow: "4px 4px 19px 0 rgba(0, 0, 0, 0.05)",
          borderLeft: "1px solid #E5E7EB",
        }}
      >
        {headerEl}

        <List ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 0 }}>
          {openNode!.children!.map(renderLeaf)}
        </List>

        {showFooter && (
          <Box sx={{ mt: "auto" }}>
            <Box sx={{ mt: 1 }}>
              <Divider />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                py: 1.5,
                px: 1.5,
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <SvgIconUrl src={SECURITYICON} width={15} height={17} color="#98A2B3" />
                <IsoText>{t("Iso17451")}</IsoText>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StausChip active={isInventoryActive}>
                  <StatusChipText active={isInventoryActive}>
                    {t(isInventoryActive ? "active" : "inactive")}
                  </StatusChipText>
                </StausChip>
                <Tooltip
                  title={
                    !isInventoryActive ? t("asYouAddedAnItemThatDoesNotBelongToTheIso174511") : ""
                  }
                  placement="top"
                  disableHoverListener={isInventoryActive}
                >
                  <span>
                    <AppSwitch disabled checked={isInventoryActive} />{" "}
                  </span>
                </Tooltip>
              </div>
            </Box>
          </Box>
        )}
      </Box>
    ) : null;
  }

  return (
    <Drawer
      anchor="left"
      open={!!openNode}
      onClose={onClose}
      hideBackdrop
      variant="persistent"
      PaperProps={{
        sx: {
          width: "fit-content",
          left,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid #E5E7EB",
          boxShadow: "none",
          zIndex: 1101,
          backgroundColor: "#F9FAFB",
        },
        "data-sidebar": "third",
      }}
    >
      {!!openNode && hasChildren && (
        <Box
          sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {headerEl}

          <Box
            sx={{
              mt: 1,
              maxHeight: "calc(100vh - 120px)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              flex: 1,
              minHeight: 0,
            }}
          >
            <List ref={listRef} sx={{ py: 0 }}>
              {openNode.children!.map(renderLeaf)}
            </List>
          </Box>

          {showFooter && (
            <Box sx={{ mt: "auto" }}>
              <Box sx={{ mt: 1 }}>
                <Divider />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  py: 1.5,
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <SvgIconUrl src={SECURITYICON} width={15} height={17} color="#98A2B3" />
                  <IsoText>{t("Iso17451")}</IsoText>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StausChip active={isInventoryActive}>
                    <StatusChipText active={isInventoryActive}>
                      {t(isInventoryActive ? "active" : "inactive")}
                    </StatusChipText>
                  </StausChip>
                  <Tooltip
                    title={
                      !isInventoryActive ? t("asYouAddedAnItemThatDoesNotBelongToTheIso174511") : ""
                    }
                    placement="top"
                    disableHoverListener={isInventoryActive}
                  >
                    <span>
                      <AppSwitch disabled checked={isInventoryActive} />{" "}
                    </span>
                  </Tooltip>
                </div>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default ThirdLevelList;
